import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Repeat } from 'lucide-react';
import { PinPad } from '@renderer/components/PinPad';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { formatPrecio } from '@renderer/utils/formatters';
import type { EntregaTurnoConDetalle } from '@shared/types';

/**
 * Sprint 172 (D-1) — **Bitácora de cambios de turno**, para la caja y la dueña.
 *
 * José: *"una bitácora de cambios de turno, que tenga código de seguridad para acceder"*. El código es el
 * PIN de quien la abre, **cada vez** que se entra a la pantalla; el servidor lo comprueba y tiene su propio
 * freno de intentos. Lo que se ve aquí no se guarda en el navegador: al salir, hay que volver a poner el PIN.
 *
 * Cada renglón dice qué mesas, de quién a quién, quién autorizó, cuánto llevaba cada cuenta y cuánta propina
 * ya se había cobrado al momento — lo que se discute en el piso cuando una mesa cambia de manos.
 */
export function CambiosDeTurnoPage() {
  const navigate = useNavigate();
  const sesion = useSessionStore((s) => s.sesion);
  const monedaSimbolo = useNegocioStore((s) => s.negocio?.monedaSimbolo ?? '$');
  const [entregas, setEntregas] = useState<EntregaTurnoConDetalle[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reset, setReset] = useState(0);
  const [abriendo, setAbriendo] = useState(false);
  const abriendoRef = useRef(false);
  // Sprint 172 (ux-master #6) — Si falla la red, no se vuelve a pedir el PIN: se reintenta con el mismo.
  const [fallaRed, setFallaRed] = useState(false);
  const pinRef = useRef('');

  const abrir = async (pin: string) => {
    if (abriendoRef.current) return;
    abriendoRef.current = true;
    setAbriendo(true);
    setError(null);
    setFallaRed(false);
    pinRef.current = pin;
    const r = await window.api.pedidos.bitacoraTurnos(pin);
    abriendoRef.current = false;
    setAbriendo(false);
    if (r.ok) {
      setEntregas(r.data);
      return;
    }
    const msg = r.error ?? '';
    if (/PIN|intentos/i.test(msg)) {
      setError(msg);
      setReset((x) => x + 1);
    } else {
      setFallaRed(true);
    }
  };

  if (!sesion) return null;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => navigate('/mesas')} className="flex min-h-[44px] items-center gap-2 rounded-lg px-3 text-base font-medium text-slate-700 hover:bg-slate-100">
          <ArrowLeft className="h-5 w-5" /> Mesas
        </button>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
          <Repeat className="h-6 w-6" /> Cambios de turno
        </h1>
      </div>

      {entregas === null ? (
        <div className="card mx-auto max-w-sm space-y-3 py-6 text-center">
          <Lock className="mx-auto h-8 w-8 text-slate-600" />
          <p className="text-base text-slate-800">Para ver la bitácora, pon tu PIN.</p>
          <PinPad label={sesion.nombre} resetSignal={reset} error={error ?? undefined} onComplete={(pin) => void abrir(pin)} />
          {abriendo && <p className="text-base text-slate-700">Abriendo…</p>}
          {fallaRed && !abriendo && (
            <div className="space-y-2">
              <p className="text-base text-slate-800">No se pudo cargar. Revisa la conexión.</p>
              <button
                type="button"
                onClick={() => void abrir(pinRef.current)}
                className="min-h-[44px] rounded-lg bg-primary px-5 text-base font-semibold text-white"
              >
                Reintentar
              </button>
            </div>
          )}
        </div>
      ) : entregas.length === 0 ? (
        <div className="card py-8 text-center text-base text-slate-700">
          Todavía no hay cambios de turno. Cuando un mesero entregue sus mesas, o tú las reasignes, quedará aquí.
        </div>
      ) : (
        <ul className="space-y-3">
          {entregas.map((e) => (
            <li key={e.id} className="card space-y-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="text-base font-semibold text-slate-900">
                  {e.modo === 'reasignacion' ? `${e.autorizoNombre} reasignó` : `${e.deNombre} entregó`}{' '}
                  {e.cantidad === 1 ? '1 mesa' : `${e.cantidad} mesas`} a {e.aNombre}
                </div>
                <div className="text-sm text-slate-700">
                  {new Date(e.fecha).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })}
                </div>
              </div>
              <div className="text-sm text-slate-700">
                {e.modo === 'reasignacion'
                  ? `Con el PIN de ${e.autorizoNombre} (${e.autorizoRol === 'dueño' ? 'dueña' : 'caja'}). Eran de ${e.deNombre}.`
                  : `Con el PIN de ${e.deNombre} y el de ${e.aNombre}.`}
              </div>
              <div className="overflow-x-auto">
              <table className="w-full min-w-[30rem] text-sm">
                <thead>
                  <tr className="text-left text-slate-700">
                    <th className="py-1 font-medium">Mesa</th>
                    <th className="py-1 font-medium">Pedido</th>
                    <th className="py-1 font-medium">Era de</th>
                    <th className="py-1 text-right font-medium">Llevaba</th>
                    <th className="py-1 text-right font-medium">Propina ya cobrada</th>
                  </tr>
                </thead>
                <tbody>
                  {e.detalle.map((d) => (
                    <tr key={d.pedidoId} className="border-t border-slate-200 text-slate-900">
                      <td className="py-1">{d.mesa ?? '—'}</td>
                      <td className="py-1 font-mono">#{String(d.folio).padStart(5, '0')}</td>
                      <td className="py-1">{d.deNombre}</td>
                      <td className="py-1 text-right font-mono">{formatPrecio(d.totalCentavos, monedaSimbolo)}</td>
                      <td className="py-1 text-right font-mono">{formatPrecio(d.propinaCobradaCentavos, monedaSimbolo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
